import classnames from 'classnames'
import React, { useCallback, useId, useRef, useState } from 'react'

import useFocusRing from '../../../utilities/useFocusRing'
import Button from '../Button/Button'
import Popup from '../Popup/Popup'

import styles from './Menu.module.scss'

export type MenuProps = Omit<React.ComponentProps<typeof Button>, 'onClick'> &
  React.PropsWithChildren<{
    label: React.ReactNode
  }>

export default React.memo(function Menu({ label, children, className, ...buttonProps }: MenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState(false)

  const doOpen = useCallback(() => {
    setOpen(true)
  }, [setOpen])

  const doClose = useCallback(() => {
    setOpen(false)
    setTimeout(() => buttonRef.current?.focus(), 0)
  }, [setOpen])

  const menuRef = useRef<HTMLUListElement | null>(null)

  const handleKeyDown = useCallback<React.KeyboardEventHandler>(
    (e) => {
      if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault()
        buttonRef.current?.click()
      }
    },
    [buttonRef]
  )

  const [focusRingProps, focusRingHelpers] = useFocusRing({ doClose, listRef: menuRef })

  const setMenuRef = useCallback(
    (e: HTMLUListElement) => {
      menuRef.current = e
      focusRingHelpers.focus()
    },
    [menuRef, focusRingHelpers.focus]
  )

  const menuId = useId()

  return (
    <div className={classnames(styles.Menu, className)}>
      <Button
        buttonRef={buttonRef as any}
        {...buttonProps}
        onClick={doOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        {label}
      </Button>
      <div onClick={doClose}>
        <Popup open={open} onRequestClose={doClose} menu className={styles.popup}>
          <ul id={menuId} role="menu" tabIndex={-1} ref={setMenuRef} {...focusRingProps}>
            {children}
          </ul>
        </Popup>
      </div>
    </div>
  )
})
